/**
 * Schools API client: the school list, one school's record, users, activity
 * and settings, and the school setup flow.
 *
 *   GET   /v1/schools              (super-admin only)
 *   GET   /v1/schools/:id
 *   GET   /v1/schools/:id/users
 *   GET   /v1/schools/:id/activity
 *   PATCH /v1/schools/:id/settings
 *   POST  /v1/schools/:id/setup
 *
 * Every per-school call sends the path school as `x-school-id`, so the request
 * school is the school in the URL and in the caller's query key.
 *
 * `GET /v1/schools/:id` answers 428 while a school still needs setup; that
 * reaches callers as an `ApiError` with status 428.
 */

import { SchoolStatusSchema } from '@school-journey/shared-types'
import {
  GetSingleSchoolResponseSchema,
  SchoolDetailSchema,
  type SchoolListItem
} from '@school-journey/shared-types/api/schools'
import { z } from 'zod'
import type { BackendClient } from '@/lib/backend-client/client'
import { path, type QueryValue } from '@/lib/backend-client/endpoint'
import { wireEnum } from '@/lib/backend-client/wire'

/** Any JSON object: the row check behind `z.custom`. */
const isRecord = (value: unknown): boolean => typeof value === 'object' && value !== null

/**
 * Extended subscription plan with additional fields from detailed response
 */
interface SubscriptionPlan {
  id: string
  name: string
  slug: string
  description: string
  status: string
}

/**
 * System group configuration
 */
interface SystemGroup {
  id: string
  name: string
  displayName: string
  audience: string
}

interface SystemGroups {
  parentsGroup?: SystemGroup
  studentsGroup?: SystemGroup
  facultyGroup?: SystemGroup
  appMessageGroup?: SystemGroup
}

/**
 * Extended school type for detailed responses
 * Base fields come from shared-types SchoolListItem, with some fields made non-nullable
 * for UI convenience (the API always returns these values in practice)
 */
export interface School extends Omit<
  SchoolListItem,
  'createdAt' | 'updatedAt' | 'subscriptionPlan' | 'name' | 'status'
> {
  /** School name - always present in practice */
  name: string
  /** School status */
  status: string
  createdAt: string
  updatedAt: string
  /**
   * Indicates whether the school has finished the initial setup / onboarding flow on the backend.
   * If this value is `false` the UI can surface a warning that the school is still being configured.
   */
  isSetupComplete?: boolean
  logoUrl?: string | null
  logoThumbnailUrl?: string | null
  logoUrlLight?: string | null
  logoUrlDark?: string | null
  subscriptionPlan?: SubscriptionPlan
  systemGroups?: SystemGroups
}

const SchoolListResponseSchema = z.object({ data: z.array(z.custom<School>(isRecord)) })

/**
 * `GET /v1/schools/:id` as the full school record (`getSchool`). The row
 * passes through whole: the shared-types school schemas describe a narrower
 * row and coerce dates.
 */
export const SchoolResponseSchema = z.object({ data: z.custom<School>(isRecord) })

/** Every school. Super-admin only (`requireSuperAdmin`); anyone else gets a 403. */
export async function listSchools(client: BackendClient): Promise<School[]> {
  const result = await client.request({ method: 'GET', path: '/v1/schools' }, SchoolListResponseSchema)
  return result.data
}

const SchoolUserListResponseSchema = z.object({
  // Each screen that lists users reads the row through its own interface
  // (stricter than the backend's nullable fields), so the row stays untyped
  // here, as it was under the legacy transport.
  data: z.array(z.custom<any>(isRecord)),
  meta: z.object({ total: z.number(), limit: z.number(), offset: z.number().optional() }).optional(),
  message: z.string().optional()
})

/**
 * One school's user list: app users by default; `query` pages it, switches to
 * platform users (`platform_users`) or filters by `roles` / `grades`.
 *
 * @see GET /v1/schools/:id/users
 */
export function listSchoolUsers(
  client: BackendClient,
  schoolId: string,
  query?: Record<string, QueryValue>
): Promise<z.infer<typeof SchoolUserListResponseSchema>> {
  return client.request(
    { method: 'GET', path: path`/v1/schools/${schoolId}/users`, query },
    SchoolUserListResponseSchema,
    { schoolId }
  )
}

// Interface for the user object in activity data
interface ActivityUser {
  id: string
  firstName: string
  lastName: string
  email: string
  pictureUrl: string | null
}

// Interface for activity data
export interface Activity {
  schoolId: string
  schoolName: string
  customerId: string
  email: string
  timestamp: number
  activityType: string
  screenName: string
  platform: string
  appVersion: string
  user: ActivityUser
}

const SchoolActivityResponseSchema = z.object({ data: z.array(z.custom<Activity>(isRecord)) })

/** The school's recent app activity, newest first. */
export async function listSchoolActivity(client: BackendClient, schoolId: string): Promise<Activity[]> {
  const result = await client.request(
    { method: 'GET', path: path`/v1/schools/${schoolId}/activity` },
    SchoolActivityResponseSchema,
    { schoolId }
  )
  return result.data
}

/**
 * Saves school settings fields. Callers read nothing back, so the answer is
 * only checked for success.
 *
 * @see PATCH /v1/schools/:id/settings
 */
export async function updateSchoolSettings(
  client: BackendClient,
  schoolId: string,
  body: Record<string, unknown>
): Promise<void> {
  await client.send({ method: 'PATCH', path: path`/v1/schools/${schoolId}/settings`, body }, { schoolId })
}

/**
 * The fields of a school's Blackbaud grade levels the dashboard reads, from
 * the detail response's `referenceData` (which shared-types leaves untyped).
 */
const ReferenceGradeLevelSchema = z.object({
  id: z.string(),
  platformId: z.number().nullable(),
  active: z.boolean().nullable(),
  abbreviation: z.string().nullable(),
  description: z.string().nullable(),
  name: z.string().nullable(),
  sortOrder: z.number().nullable()
})
export type ReferenceGradeLevel = z.infer<typeof ReferenceGradeLevelSchema>

const ReferenceSchoolLevelSchema = z.object({
  id: z.string(),
  platformId: z.number().nullable(),
  abbreviation: z.string().nullable(),
  name: z.string().nullable(),
  sortOrder: z.number().nullable()
})
export type ReferenceSchoolLevel = z.infer<typeof ReferenceSchoolLevelSchema>

/**
 * The shared school detail plus what the dashboard reads beyond it: the legacy
 * logo columns and the level reference data. The status enum lands an unknown
 * member on `unrecognised_status` instead of failing the parse.
 */
const SchoolDetailWireSchema = SchoolDetailSchema.extend({
  status: wireEnum(SchoolStatusSchema, 'unrecognised_status'),
  logoUrlLight: z.string().nullable().optional(),
  logoUrlDark: z.string().nullable().optional(),
  referenceData: z
    .object({
      gradeLevels: z.array(ReferenceGradeLevelSchema).optional(),
      levels: z.array(ReferenceSchoolLevelSchema).optional()
    })
    .nullable()
    .optional()
})

/**
 * `GET /v1/schools/:id` (`getSchool`) parsed through the shared school detail
 * (dates coerced), with the logos and level reference data the school
 * settings and design screens read.
 */
export const SchoolDetailResponseSchema = GetSingleSchoolResponseSchema.extend({ data: SchoolDetailWireSchema })
export type SchoolDetailResponse = z.infer<typeof SchoolDetailResponseSchema>

/** The slice of `GET /v1/schools/:id` the setup check reads: with `platformSyncReport`, one row per setup sync. */
export const SchoolSetupStatusSchema = z.object({
  data: z.object({ platformSyncReport: z.array(z.looseObject({})).optional() }).optional()
})

/** The slice of `POST /v1/schools/:id/setup` the setup screen reads. */
const SchoolSetupResponseSchema = z.object({
  data: z.object({ setupJob: z.unknown().optional() }).optional()
})

/** What `GET /v1/schools/:id` adds to the school on request. */
export interface GetSchoolOptions {
  /** Also return the setup syncs' progress */
  platformSyncReport?: boolean
}

/**
 * Reads one school, sent for that school. Screens read different slices of
 * the response, so each passes the schema for its slice (the named `*Schema`
 * exports here). Throws an `ApiError` with status 428 while the school still
 * needs setup.
 *
 * @see GET /v1/schools/:id
 */
export function getSchool<T>(
  client: BackendClient,
  schoolId: string,
  schema: z.ZodType<T>,
  options: GetSchoolOptions = {}
): Promise<T> {
  return client.request(
    {
      method: 'GET',
      path: path`/v1/schools/${schoolId}`,
      query: { platform_sync_report: options.platformSyncReport || undefined }
    },
    schema,
    { schoolId }
  )
}

/**
 * Starts a school's setup. Returns at once with the queued setup job; the
 * syncs run in the background.
 *
 * @see POST /v1/schools/:id/setup
 */
export async function startSchoolSetup(client: BackendClient, schoolId: string): Promise<{ setupJob?: unknown }> {
  const result = await client.request(
    { method: 'POST', path: path`/v1/schools/${schoolId}/setup`, body: {} },
    SchoolSetupResponseSchema,
    { schoolId }
  )
  return { setupJob: result.data?.setupJob }
}
